import React from "react";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { CreditCard, DollarSign, Calendar, Plus, XCircle } from "lucide-react";
import { subscriptionSchema } from "../../schemas/subscriptionFormSchema";
import { usePet } from "../../hooks/usePet";
import Select from "react-select";
import { Controller } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

export default function SubscriptionForm({
  mode = "view",
  defaultValues = {
    name: "",
    description: "",
    price: 0,
    billing_cycle: "N/A",
    features: [{ title: "", status: "active", _id: "" }],
    petIds: [],
  },
  onSubmit,
  onCancel,
}) {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm({
    context: {
      defaultPrice: defaultValues?.price || 0,
    },
    resolver: yupResolver(subscriptionSchema),
    defaultValues,
  });

  const DURATION_DETAILS = {
    Weekly : 7,
    Monthly : 30,
    Yearly : 365
  }

  // Wrap onSubmit to transform petIds to pet array with pet_id objects
  const handleFormSubmit = (data) => {
    const transformedData = {
      ...data,
      pet: data.petIds ? data.petIds.map((id) => ({ pet_id: id })) : [],
    };
    delete transformedData.petIds;
    onSubmit(transformedData);
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: "features",
  });

  const { fetchPet } = usePet();
  const petQuery = useQuery({
    queryKey: ["pets"],
    queryFn: fetchPet,
  });

  useEffect(() => {
    if (defaultValues?.choosenpets) {
      const choosenPetIds = defaultValues.choosenpets.map((pet) => pet._id);
      setValue("petIds", choosenPetIds);
    }
  }, [defaultValues?.choosenpets, setValue]);
  const billingCycle = useWatch({control, name : "price"});
  useEffect(() => {
    setValue("duration", DURATION_DETAILS[billingCycle])
  }, [billingCycle]);

  const petOptions =
    petQuery.data?.data?.pet?.map((pet) => ({
      value: pet._id,
      label: pet.title,
      image: pet.image,
    })) || [];

  const customOption = (props) => {
    const { data, innerRef, innerProps, isDisabled, isFocused, isSelected } =
      props;
    return !isDisabled ? (
      <div
        ref={innerRef}
        {...innerProps}
        className={`flex justify-between items-center px-3 py-2 cursor-pointer ${
          isFocused ? "bg-blue-100" : ""
        } ${isSelected ? "bg-blue-200 font-semibold" : ""}`}
      >
        <div>{data.label}</div>
        {data.image && (
          <img
            src={data.image}
            alt={data.label}
            className="w-8 h-8 rounded-full object-cover ml-2"
          />
        )}
      </div>
    ) : null;
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      {/* Subscription Name */}
      <div className="p-6 space-y-6">
        <div>
          <label className="block text-sm font-medium mb-1">
            Subscription Name
          </label>
          <div className="relative">
            <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              {...register("title")}
              readOnly={mode === "view"}
              className="pl-10 pr-3 py-2 border rounded w-full"
              placeholder="E.g., Premium Monthly"
            />
          </div>
          {errors.name && (
            <p className="text-red-500 text-xs">{errors.name.message}</p>
          )}
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium mb-1">Description</label>
          <textarea
            {...register("description")}
            readOnly={mode === "view"}
            className="px-3 py-2 border rounded w-full"
            placeholder="Describe the subscription plan"
            rows={2}
          />
          {errors.description && (
            <p className="text-red-500 text-xs">{errors.description.message}</p>
          )}
        </div>

        {/* Price & Billing Cycle */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Price</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <DollarSign className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="number"
                step="0.01"
                {...register("price")}
                readOnly={mode === "view"}
                className="pl-10 pr-3 py-2 border rounded w-full"
                placeholder="0.00"
              />
            </div>
            {errors.price && (
              <p className="text-red-500 text-xs">{errors.price.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Billing Cycle
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Calendar className="h-5 w-5 text-gray-400" />
              </div>
              <select
                {...register("billing_cycle")}
                disabled={mode === "view"}
                className="pl-10 pr-3 py-2 border rounded w-full"
              >
                <option value="">Select billing cycle</option>
                <option value="N/A">N/A (Free Plan)</option>
                <option value="Monthly">Monthly</option>
                <option value="Yearly">Yearly</option>
                <option value="Weekly">Weekly</option>
              </select>
            </div>
            {errors.billing_cycle && (
              <p className="text-red-500 text-xs">
                {errors.billing_cycle.message}
              </p>
            )}
          </div>
          {mode !== "view" && (
            <>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="consent"
                  {...register("consent")}
                  disabled={mode === "view"}
                  className="h-4 w-4 border border-gray-300 rounded"
                />
                <label htmlFor="consent" className="text-sm">
                  I agree that I have updated the price in both Play store
                  Console and Apple store console
                </label>
              </div>
              {errors.consent && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.consent.message}
                </p>
              )}
            </>
          )}
        </div>

        {/* Features (array of objects) */}
        <div>
          <label className="block text-sm font-medium mb-1">Features</label>
          <div className="border rounded p-4 space-y-2">
            {fields.map((field, index) => (
              <div
                key={`features.${index}._id`}
                className="grid grid-cols-4 gap-2 items-center"
              >
                {/* Hidden _id (so that when you submit, the original ID is preserved) */}
                <input
                  type="hidden"
                  {...register(`features.${index}._id`)}
                  defaultValue={field._id}
                />

                {/* Title */}
                <div className="col-span-2">
                  <input
                    {...register(`features.${index}.title`)}
                    readOnly={mode === "view"}
                    className="w-full px-3 py-1 border rounded"
                    placeholder="Feature title"
                    defaultValue={field.title}
                  />
                  {errors.features?.[index]?.title && (
                    <p className="text-red-500 text-xs">
                      {errors.features[index].title?.message}
                    </p>
                  )}
                </div>

                {/* Status */}
                {/* <div className="col-span-1">
                  <select
                    {...register(`features.${index}.status`)}
                    disabled={mode === "view"}
                    className="w-full px-2 py-1 border rounded"
                    defaultValue={field.status}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  {errors.features?.[index]?.status && (
                    <p className="text-red-500 text-xs">
                      {errors.features[index].status?.message}
                    </p>
                  )}
                </div> */}

                {/* Remove button (only if not in “view” mode) */}
                {mode !== "view" && (
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="h-5 w-5 text-red-500 hover:text-red-700 ml-2"
                  >
                    <XCircle className="h-5 w-5" />
                  </button>
                )}
              </div>
            ))}

            {/* Add Feature button (only when editing/creating) */}
            {mode !== "view" && (
              <button
                type="button"
                onClick={() =>
                  append({
                    title: "",
                    status: "active",
                    _id: "", // if your backend generates IDs, you can leave this blank
                  })
                }
                className="flex items-center text-primary hover:text-primary-700 text-sm font-medium"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Feature
              </button>
            )}
          </div>
          {typeof errors.features === "string" && (
            <p className="text-red-500 text-xs">{errors.features}</p>
          )}
        </div>

        {/* Pet Multi-Select */}
        <div>
          <label className="block text-sm font-medium mb-1">Assign Pets</label>
          {petQuery.isLoading ? (
            <p className="text-sm text-gray-500">Loading pets...</p>
          ) : petQuery.isError ? (
            <p className="text-sm text-red-500">Failed to load pets</p>
          ) : (
            <Controller
              name="petIds"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  options={petOptions}
                  isMulti
                  isDisabled={mode === "view"}
                  className="w-full"
                  classNamePrefix="react-select"
                  components={{ Option: customOption }}
                  onChange={(selected) => {
                    field.onChange(
                      selected ? selected.map((item) => item.value) : []
                    );
                  }}
                  value={petOptions.filter((option) =>
                    field.value?.includes(option.value)
                  )}
                />
              )}
            />
          )}
          {errors.petIds && (
            <p className="text-red-500 text-xs">{errors.petIds.message}</p>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="px-6 py-4 border-t flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          {mode === "view" ? "Close" : "Cancel"}
        </button>
        {mode !== "view" && (
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 rounded-md text-white bg-primary hover:bg-primary-700"
          >
            {mode === "edit" ? "Save Changes" : "Create Plan"}
          </button>
        )}
      </div>
    </form>
  );
}
